import { createFileRoute } from "@tanstack/react-router";
import { ExternalAuthorityTab } from "@/components/admin/ExternalAuthority";
import {
  AiDiscoveryTab,
  EarnedPipelineTab,
  IndexQueueTab,
  ProfileMatrixTab,
  SearchOpsTab,
} from "@/components/admin/AuthorityOps";
import {
  ChecklistTab,
  FeedsTab,
  SearchEnginesTab,
  TimelineTab,
  WeeklyReportTab,
} from "@/components/admin/AuthorityPart4";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  AdminButton,
  AdminDialog,
  Field,
  Input,
  Row,
  TableShell,
  Textarea,
} from "@/components/admin/AdminUI";
import {
  MENTION_KINDS,
  MENTION_STATUSES,
  MONITORED_ENDPOINTS,
  RELEASE_TASKS,
  SUBMISSION_STATUSES,
  SUBMISSION_TARGETS,
  TASK_GROUPS,
  TASK_STATUSES,
} from "@/lib/authority";
import {
  deleteMention,
  deleteSubmission,
  getAuthorityOverview,
  getReleasePromotion,
  listMentions,
  listPromotableReleases,
  listSubmissions,
  runIndexScan,
  saveMention,
  saveReleaseTask,
  saveSubmission,
  type MentionRow,
  type SubmissionRow,
} from "@/lib/authority.functions";

export const Route = createFileRoute("/_authenticated/admin/authority")({
  head: () => ({
    meta: [
      { title: "Authority Engine — Buddha KA Admin" },
      { name: "description", content: "Submission center, mentions registry, release promotion, and measured index health for buddhaka.com." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthorityAdmin,
});

const TABS = [
  "dashboard",
  "submissions",
  "mentions",
  "releases",
  "health",
  "engines",
  "search-ops",
  "queue",
  "profiles",
  "earned",
  "ai-discovery",
  "feeds",
  "timeline",
  "checklist",
  "external",
  "report",
] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  dashboard: "Dashboard",
  submissions: "Submission center",
  mentions: "Mentions & backlinks",
  releases: "Release promotion",
  health: "Index health",
  engines: "Search engines",
  "search-ops": "Search ops",
  queue: "Priority queue",
  profiles: "Profile matrix",
  earned: "Earned authority",
  "ai-discovery": "AI discovery",
  feeds: "Feed monitoring",
  timeline: "Authority timeline",
  checklist: "Checklist",
  external: "External graph",
  report: "Weekly report",
};


const selectClass =
  "w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold";

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border hairline p-4">
      <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-serif text-3xl text-gold-luminous">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground/70">{hint}</div>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg border hairline p-6 text-sm text-muted-foreground">{children}</p>;
}

function AuthorityAdmin() {
  const [tab, setTab] = useState<Tab>("dashboard");
  return (
    <div>
      <header>
        <h2 className="font-serif text-2xl text-gold-luminous">External Authority Engine</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Every number here is either recorded by a steward or measured live against our own
          endpoints. Nothing is estimated, and no third-party metric is invented.
        </p>
      </header>

      <div role="tablist" aria-label="Authority sections" className="mt-6 flex flex-wrap gap-2 border-b hairline pb-4">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] transition ${
              tab === t ? "bg-gold-gradient text-obsidian" : "border hairline text-muted-foreground hover:border-gold"
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "dashboard" && <DashboardTab />}
        {tab === "submissions" && <SubmissionsTab />}
        {tab === "mentions" && <MentionsTab />}
        {tab === "releases" && <ReleasesTab />}
        {tab === "health" && <HealthTab />}
        {tab === "engines" && <SearchEnginesTab />}
        {tab === "search-ops" && <SearchOpsTab />}
        {tab === "queue" && <IndexQueueTab />}
        {tab === "profiles" && <ProfileMatrixTab />}
        {tab === "earned" && <EarnedPipelineTab />}
        {tab === "ai-discovery" && <AiDiscoveryTab />}
        {tab === "feeds" && <FeedsTab />}
        {tab === "timeline" && <TimelineTab />}
        {tab === "checklist" && <ChecklistTab />}
        {tab === "external" && <ExternalAuthorityTab />}
        {tab === "report" && <WeeklyReportTab />}
      </div>
    </div>
  );
}

/* ── Dashboard ───────────────────────────────────────────────────────── */

function DashboardTab() {
  const fetchOverview = useServerFn(getAuthorityOverview);
  const { data, isLoading, error } = useQuery({
    queryKey: ["authority", "overview"],
    queryFn: () => fetchOverview(),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading authority state…</p>;
  if (error) return <Empty>Could not load the authority overview: {(error as Error).message}</Empty>;
  if (!data) return null;

  const scan = data.scan;
  const critical = (scan?.issues ?? []).filter((i) => i.severity === "error").length;
  const warnings = (scan?.issues ?? []).filter((i) => i.severity === "warn").length;

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Targets tracked"
          value={`${data.submissions.tracked}/${data.submissions.available}`}
          hint={`${data.submissions.byStatus.verified ?? 0} verified`}
        />
        <Stat label="Mentions logged" value={data.mentions.total} hint={`${data.mentions.published} live`} />
        <Stat label="Referring domains" value={data.mentions.referringDomains} hint="Distinct, from logged URLs" />
        <Stat
          label="Index issues"
          value={scan ? critical + warnings : "—"}
          hint={scan ? `${critical} errors · ${warnings} warnings` : "No scan run yet"}
        />
      </div>

      <section>
        <h3 className="font-serif text-lg text-gold-luminous">Last measured scan</h3>
        {scan ? (
          <div className="mt-3 rounded-lg border hairline p-4 text-sm text-muted-foreground">
            <p>
              {new Date(scan.created_at).toLocaleString()} — {scan.pages_scanned} pages sampled from{" "}
              <span className="text-foreground">{scan.origin}</span>
              {scan.duration_ms ? ` in ${(scan.duration_ms / 1000).toFixed(1)}s` : ""}.
            </p>
          </div>
        ) : (
          <div className="mt-3">
            <Empty>No index health scan has been run yet. Open the Index health tab and run one.</Empty>
          </div>
        )}
      </section>

      <section>
        <h3 className="font-serif text-lg text-gold-luminous">Verified submissions</h3>
        {data.submissions.verifiedTargets.length ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.submissions.verifiedTargets.map((key) => (
              <li key={key} className="rounded-full border border-emerald/30 bg-emerald/10 px-3 py-1 text-xs text-emerald">
                {SUBMISSION_TARGETS.find((t) => t.key === key)?.label ?? key}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-3">
            <Empty>Nothing verified yet. Record a submission once a console confirms it.</Empty>
          </div>
        )}
      </section>
    </div>
  );
}

/* ── Submissions ─────────────────────────────────────────────────────── */

const emptySubmission = {
  target_key: SUBMISSION_TARGETS[0]!.key,
  url: "",
  status: "not_started",
  submitted_at: "",
  verified_at: "",
  last_checked_at: "",
  notes: "",
};

function SubmissionsTab() {
  const qc = useQueryClient();
  const fetchRows = useServerFn(listSubmissions);
  const save = useServerFn(saveSubmission);
  const remove = useServerFn(deleteSubmission);
  const [editing, setEditing] = useState<null | (typeof emptySubmission & { id?: string })>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["authority", "submissions"],
    queryFn: () => fetchRows(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["authority"] });
    setEditing(null);
  };

  const saveMut = useMutation({
    mutationFn: (payload: typeof emptySubmission & { id?: string }) => save({ data: payload }),
    onSuccess: () => {
      toast.success("Submission saved");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Submission removed");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const byKey = new Map(rows.map((r: SubmissionRow) => [r.target_key, r]));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Each target lists the console a steward works in and the first-party URL we submit.
          Status is recorded by hand because these consoles have no read API here.
        </p>
        <AdminButton onClick={() => setEditing({ ...emptySubmission })}>Record submission</AdminButton>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <TableShell
          headers={["Target", "Submits", "Status", "Submitted", "Verified", ""]}
          rows={SUBMISSION_TARGETS.map((target) => {
            const row = byKey.get(target.key);
            return (
              <tr key={target.key} className="align-top">
                <td className="px-4 py-3">
                  <div className="text-foreground">{target.label}</div>
                  <div className="text-xs text-muted-foreground/70">{target.note}</div>
                  <a
                    href={target.console}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-1 inline-block text-xs text-gold underline-offset-4 hover:underline"
                  >
                    Open console
                  </a>
                </td>
                <td className="px-4 py-3">
                  <a href={target.submits} className="text-xs text-muted-foreground underline-offset-4 hover:text-gold hover:underline">
                    {target.submits}
                  </a>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {SUBMISSION_STATUSES.find((s) => s.value === (row?.status ?? "not_started"))?.label}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{row?.submitted_at ?? "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{row?.verified_at ?? "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        setEditing({
                          id: row?.id,
                          target_key: target.key,
                          url: row?.url ?? "",
                          status: row?.status ?? "not_started",
                          submitted_at: row?.submitted_at ?? "",
                          verified_at: row?.verified_at ?? "",
                          last_checked_at: row?.last_checked_at ?? "",
                          notes: row?.notes ?? "",
                        })
                      }
                    >
                      {row ? "Edit" : "Record"}
                    </AdminButton>
                    {row && (
                      <AdminButton variant="danger" onClick={() => delMut.mutate(row.id)}>
                        Clear
                      </AdminButton>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        />
      )}

      <AdminDialog open={Boolean(editing)} onClose={() => setEditing(null)} title="Submission record">
        {editing && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveMut.mutate(editing);
            }}
          >
            <Field label="Target">
              <select
                className={selectClass}
                value={editing.target_key}
                onChange={(e) => setEditing({ ...editing, target_key: e.target.value })}
              >
                {SUBMISSION_TARGETS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Row>
              <Field label="Status">
                <select
                  className={selectClass}
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                >
                  {SUBMISSION_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Submitted URL" hint="Defaults to the target's first-party URL.">
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
              </Field>
            </Row>
            <Row>
              <Field label="Submitted on">
                <Input type="date" value={editing.submitted_at} onChange={(e) => setEditing({ ...editing, submitted_at: e.target.value })} />
              </Field>
              <Field label="Verified on">
                <Input type="date" value={editing.verified_at} onChange={(e) => setEditing({ ...editing, verified_at: e.target.value })} />
              </Field>
            </Row>
            <Field label="Last checked">
              <Input type="date" value={editing.last_checked_at} onChange={(e) => setEditing({ ...editing, last_checked_at: e.target.value })} />
            </Field>
            <Field label="Notes">
              <Textarea rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <AdminButton type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</AdminButton>
              <AdminButton type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? "Saving…" : "Save"}
              </AdminButton>
            </div>
          </form>
        )}
      </AdminDialog>
    </div>
  );
}

/* ── Mentions ────────────────────────────────────────────────────────── */

const emptyMention = {
  kind: "press",
  outlet: "",
  title: "",
  url: "",
  published_at: "",
  status: "outreach",
  contact: "",
  notes: "",
};

function MentionsTab() {
  const qc = useQueryClient();
  const fetchRows = useServerFn(listMentions);
  const save = useServerFn(saveMention);
  const remove = useServerFn(deleteMention);
  const [editing, setEditing] = useState<null | (typeof emptyMention & { id?: string })>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["authority", "mentions"],
    queryFn: () => fetchRows(),
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ["authority"] });
    setEditing(null);
  };
  const saveMut = useMutation({
    mutationFn: (payload: typeof emptyMention & { id?: string }) => save({ data: payload as never }),
    onSuccess: () => {
      toast.success("Mention saved");
      done();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Mention removed");
      done();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          A hand-kept registry of outreach, coverage, and referring domains. Only links we can
          point at are counted — nothing is inferred from a backlink index.
        </p>
        <AdminButton onClick={() => setEditing({ ...emptyMention })}>Log mention</AdminButton>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <Empty>No mentions logged yet. Start with the outreach you have already sent.</Empty>
      ) : (
        <TableShell
          headers={["Outlet", "Kind", "Status", "Published", "Domain", ""]}
          rows={rows.map((m: MentionRow) => (
            <tr key={m.id}>
              <td className="px-4 py-3">
                <div className="text-foreground">{m.outlet}</div>
                {m.title && <div className="text-xs text-muted-foreground/70">{m.title}</div>}
                {m.url && (
                  <a href={m.url} target="_blank" rel="noreferrer noopener" className="text-xs text-gold underline-offset-4 hover:underline">
                    Open link
                  </a>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {MENTION_KINDS.find((k) => k.value === m.kind)?.label ?? m.kind}
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {MENTION_STATUSES.find((s) => s.value === m.status)?.label ?? m.status}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{m.published_at ?? "—"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{m.referring_domain ?? "—"}</td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <AdminButton
                    variant="ghost"
                    onClick={() =>
                      setEditing({
                        id: m.id,
                        kind: m.kind,
                        outlet: m.outlet,
                        title: m.title ?? "",
                        url: m.url ?? "",
                        published_at: m.published_at ?? "",
                        status: m.status,
                        contact: m.contact ?? "",
                        notes: m.notes ?? "",
                      })
                    }
                  >
                    Edit
                  </AdminButton>
                  <AdminButton variant="danger" onClick={() => delMut.mutate(m.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      )}

      <AdminDialog open={Boolean(editing)} onClose={() => setEditing(null)} title="Mention record">
        {editing && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveMut.mutate(editing);
            }}
          >
            <Row>
              <Field label="Outlet">
                <Input required value={editing.outlet} onChange={(e) => setEditing({ ...editing, outlet: e.target.value })} />
              </Field>
              <Field label="Kind">
                <select className={selectClass} value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {MENTION_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </Field>
            </Row>
            <Field label="Title">
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </Field>
            <Field label="URL" hint="The referring domain is derived from this link.">
              <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
            </Field>
            <Row>
              <Field label="Status">
                <select className={selectClass} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {MENTION_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Published on">
                <Input type="date" value={editing.published_at} onChange={(e) => setEditing({ ...editing, published_at: e.target.value })} />
              </Field>
            </Row>
            <Field label="Contact">
              <Input value={editing.contact} onChange={(e) => setEditing({ ...editing, contact: e.target.value })} />
            </Field>
            <Field label="Notes">
              <Textarea rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <AdminButton type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</AdminButton>
              <AdminButton type="submit" disabled={saveMut.isPending}>{saveMut.isPending ? "Saving…" : "Save"}</AdminButton>
            </div>
          </form>
        )}
      </AdminDialog>
    </div>
  );
}

/* ── Release promotion ───────────────────────────────────────────────── */

function ReleasesTab() {
  const qc = useQueryClient();
  const fetchReleases = useServerFn(listPromotableReleases);
  const fetchPromotion = useServerFn(getReleasePromotion);
  const saveTask = useServerFn(saveReleaseTask);
  const [slug, setSlug] = useState<string>("");

  const { data: releases = [], isLoading } = useQuery({
    queryKey: ["authority", "releases"],
    queryFn: () => fetchReleases(),
  });

  const active = slug || releases[0]?.slug || "";

  const promotion = useQuery({
    queryKey: ["authority", "promotion", active],
    queryFn: () => fetchPromotion({ data: { slug: active } }),
    enabled: Boolean(active),
  });

  const taskMut = useMutation({
    mutationFn: (payload: { task_key: string; status: string; notes?: string }) =>
      saveTask({ data: { release_slug: active, ...payload } as never }),
    onSuccess: () => {
      toast.success("Task updated");
      qc.invalidateQueries({ queryKey: ["authority", "promotion", active] });
      qc.invalidateQueries({ queryKey: ["authority", "overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading releases…</p>;
  if (!releases.length) return <Empty>No releases exist yet, so there is nothing to promote.</Empty>;

  const tasks = promotion.data?.tasks ?? [];
  const measured = tasks.filter((t) => t.measured !== null);
  const measuredDone = measured.filter((t) => t.measured).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[16rem]">
          <Field label="Release">
            <select className={selectClass} value={active} onChange={(e) => setSlug(e.target.value)}>
              {releases.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.title}{r.release_date ? ` — ${r.release_date}` : ""}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {promotion.data && (
          <p className="text-sm text-muted-foreground">
            {measuredDone}/{measured.length} verification checks passing ·{" "}
            {tasks.filter((t) => t.status === "done").length}/{tasks.length} tasks done
          </p>
        )}
      </div>

      {promotion.isLoading && <p className="text-sm text-muted-foreground">Measuring release surfaces…</p>}
      {promotion.error && <Empty>Could not load this release: {(promotion.error as Error).message}</Empty>}

      {promotion.data &&
        TASK_GROUPS.map((group) => {
          const groupTasks = RELEASE_TASKS.filter((t) => t.group === group.value);
          if (!groupTasks.length) return null;
          return (
            <section key={group.value}>
              <h3 className="font-serif text-lg text-gold-luminous">{group.label}</h3>
              <ul className="mt-3 space-y-3">
                {groupTasks.map((def) => {
                  const state = tasks.find((t) => t.key === def.key);
                  if (!state) return null;
                  return (
                    <li key={def.key} className="rounded-lg border hairline p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-foreground">{def.label}</span>
                            {state.measured !== null && (
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                                  state.measured
                                    ? "border-emerald/30 bg-emerald/10 text-emerald"
                                    : "border-red-500/30 bg-red-500/10 text-red-300"
                                }`}
                              >
                                {state.measured ? "verified" : "not detected"}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">{def.detail}</p>
                          {state.evidence && (
                            <p className="mt-1 text-xs text-muted-foreground/70">Evidence: {state.evidence}</p>
                          )}
                        </div>
                        <select
                          aria-label={`${def.label} status`}
                          className={`${selectClass} w-auto`}
                          value={state.status}
                          onChange={(e) => taskMut.mutate({ task_key: def.key, status: e.target.value })}
                        >
                          {TASK_STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
    </div>
  );
}

/* ── Index health ────────────────────────────────────────────────────── */

function HealthTab() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getAuthorityOverview);
  const scanNow = useServerFn(runIndexScan);

  const { data } = useQuery({ queryKey: ["authority", "overview"], queryFn: () => fetchOverview() });
  const scanMut = useMutation({
    mutationFn: () => scanNow(),
    onSuccess: () => {
      toast.success("Scan complete");
      qc.invalidateQueries({ queryKey: ["authority"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const scan = data?.scan ?? null;
  const summary = (scan?.summary ?? {}) as Record<string, number | string | null>;
  const endpoints = (summary.endpoints ?? []) as unknown as Array<{
    path: string;
    label: string;
    status: number | null;
    ok: boolean;
    items?: number;
    error?: string | null;
  }>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          The scan fetches our own sitemaps, feeds, and a sample of pages, then reports canonical,
          schema, and internal-link facts. Orphan counts are candidates within the sample, not a
          full-site crawl verdict.
        </p>
        <AdminButton onClick={() => scanMut.mutate()} disabled={scanMut.isPending}>
          {scanMut.isPending ? "Scanning…" : "Run scan"}
        </AdminButton>
      </div>

      {!scan ? (
        <Empty>No scan has been recorded yet.</Empty>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Pages sampled" value={scan.pages_scanned} />
            <Stat label="Sitemap URLs" value={String(summary.sitemapUrls ?? "—")} />
            <Stat label="Broken canonicals" value={String(summary.brokenCanonicals ?? 0)} />
            <Stat label="Orphan candidates" value={String(summary.orphanPages ?? 0)} />
          </div>

          <section>
            <h3 className="font-serif text-lg text-gold-luminous">Monitored endpoints</h3>
            <div className="mt-3">
              <TableShell
                headers={["Endpoint", "Status", "Items"]}
                rows={(endpoints.length ? endpoints : MONITORED_ENDPOINTS.map((e) => ({ ...e, status: null, ok: false, items: undefined, error: "not scanned" }))).map((e) => (
                  <tr key={e.path}>
                    <td className="px-4 py-3">
                      <a href={e.path} className="text-foreground underline-offset-4 hover:text-gold hover:underline">{e.label}</a>
                      <div className="text-xs text-muted-foreground/70">{e.path}</div>
                    </td>
                    <td className={`px-4 py-3 text-xs ${e.ok ? "text-emerald" : "text-red-300"}`}>
                      {e.status ?? e.error ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{e.items ?? "—"}</td>
                  </tr>
                ))}
              />
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-gold-luminous">Issues</h3>
            {scan.issues.length === 0 ? (
              <div className="mt-3"><Empty>No issues detected in the last scan.</Empty></div>
            ) : (
              <ul className="mt-3 space-y-2">
                {scan.issues.map((issue, i) => (
                  <li key={`${issue.path}-${i}`} className="rounded-lg border hairline p-3 text-sm">
                    <span
                      className={`mr-3 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                        issue.severity === "error"
                          ? "border-red-500/30 bg-red-500/10 text-red-300"
                          : issue.severity === "warn"
                            ? "border-gold/30 bg-gold/10 text-gold"
                            : "border hairline text-muted-foreground"
                      }`}
                    >
                      {issue.severity}
                    </span>
                    <span className="text-foreground">{issue.message}</span>
                    <span className="ml-2 text-xs text-muted-foreground/70">{issue.path}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
